'use es6';

import { useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import UIList from 'UIComponents/list/UIList';
import UIButton from 'UIComponents/button/UIButton';
import I18n from 'I18n';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIColumnWrapper from 'UIComponents/column/UIColumnWrapper';
import UIColumn from 'UIComponents/column/UIColumn';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { PreviewConfigContext } from 'customer-data-sidebar/universal/context/PreviewConfigContext';
import { HostAppCallbacksContext } from 'customer-data-sidebar/universal/context/HostAppCallbacksContext';
import { useDeleteCrmObject } from 'customer-data-sidebar/universal/hooks/useDeleteCrmObject';
import UIConfirmModal from 'UIComponents/dialog/UIConfirmModal';
import ObjectTypeIdType from 'customer-data-objects-ui-components/propTypes/ObjectTypeIdType';
import { SidebarSubjectContext } from '../context/SidebarSubjectContext';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { PermissionsContext } from '../context/PermissionsContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CrmObjectActionsDropdown = ({
  objectTypeId,
  objectId
}) => {
  const [showModal, setShowModal] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const {
    definition: {
      singularForm
    },
    primaryDisplayLabel
  } = useContext(SidebarSubjectContext);
  const {
    closeSidebar,
    fromObjectType,
    fromObjectId
  } = useContext(PreviewConfigContext);
  const {
    onObjectDelete
  } = useContext(HostAppCallbacksContext);
  const {
    canEditSubject
  } = useContext(PermissionsContext);
  const handleDeleteSuccess = useCallback(() => {
    setDeletePending(false);
    if (typeof onObjectDelete === 'function') {
      onObjectDelete({
        objectTypeId,
        objectId
      });
    }
    closeSidebar();
  }, [objectTypeId, objectId, closeSidebar, onObjectDelete]);
  const handleDeleteError = useCallback(() => {
    setDeletePending(false);
    setDeleteFailed(true);
  }, [setDeletePending, setDeleteFailed]);
  const handleDeleteCrmObject = useDeleteCrmObject({
    fromObjectType,
    fromObjectId,
    toObjectType: objectTypeId,
    toObjectId: objectId,
    onSuccess: handleDeleteSuccess,
    onError: handleDeleteError
  });
  const handleBeginDelete = useCallback(() => {
    setDeleteFailed(false);
    setDeletePending(true);
    handleDeleteCrmObject();
  }, [setDeleteFailed, setDeletePending, handleDeleteCrmObject]);
  const openModal = useCallback(() => {
    setShowModal(true);
  }, [setShowModal]);
  const closeModal = useCallback(() => {
    setShowModal(false);
  }, [setShowModal]);
  const ConfirmDeleteButton = props => {
    const confirmLabel = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "universalSidebar.crmObjectActionsDropdown.deleteModal.deleteConfirm.object",
      options: {
        singularForm
      }
    });
    return /*#__PURE__*/_jsx(UILoadingButton, Object.assign({
      loading: deletePending,
      failed: deleteFailed
    }, props, {
      children: confirmLabel
    }));
  };
  const renderConfirmDeleteModal = () => {
    const message = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "universalSidebar.crmObjectActionsDropdown.deleteModal.deleteTitle.object",
      options: {
        singularForm
      }
    });
    const cancel = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "universalSidebar.crmObjectActionsDropdown.deleteModal.cancel"
    });
    const description = /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "universalSidebar.crmObjectActionsDropdown.deleteModal.deleteBody.object",
      options: {
        objectLabel: primaryDisplayLabel
      }
    });
    return /*#__PURE__*/_jsx(UIConfirmModal, {
      ConfirmButton: ConfirmDeleteButton,
      description: description,
      confirmUse: "danger",
      message: message,
      size: "auto",
      use: "danger",
      "data-selenium-test": "cobject-delete-modal",
      onConfirm: handleBeginDelete,
      onReject: closeModal,
      rejectLabel: cancel
    });
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "display-flex",
    children: [/*#__PURE__*/_jsxs(UIColumnWrapper, {
      align: "center",
      children: [/*#__PURE__*/_jsx(UIColumn, {}), /*#__PURE__*/_jsx(UIColumn, {
        children: /*#__PURE__*/_jsx(UIDropdown, {
          menuWidth: 200,
          placement: "bottom right",
          buttonText: I18n.text('universalSidebar.crmObjectActionsDropdown.actions'),
          buttonUse: "transparent",
          "data-selenium-test": "cobject-actions-dropdown",
          children: /*#__PURE__*/_jsx(UIList, {
            children: /*#__PURE__*/_jsx(UITooltip, {
              disabled: canEditSubject,
              "data-unit-test": "cobject-delete-button-tooltip",
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "universalSidebar.crmObjectActionsDropdown.unauthorizedDelete"
              }),
              children: /*#__PURE__*/_jsx(UIButton, {
                "data-selenium-test": "cobject-delete-button",
                "data-unit-test": "cobject-delete-button",
                onClick: openModal,
                use: "link",
                disabled: !canEditSubject,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "universalSidebar.crmObjectActionsDropdown.deleteObject"
                })
              })
            })
          })
        })
      })]
    }), showModal && renderConfirmDeleteModal()]
  });
};
CrmObjectActionsDropdown.propTypes = {
  objectTypeId: ObjectTypeIdType.isRequired,
  objectId: PropTypes.string.isRequired
};
export default CrmObjectActionsDropdown;